import React, { useState } from 'react';
import { Button } from './ui/button';
import { Camera, X, RotateCcw } from 'lucide-react';
import { uploadUserAvatar, removeUserAvatar } from '../services/api';
import { useUser } from '../hooks/useUser';
import config from '../config/environment';

interface AvatarUploadProps {
  currentAvatar?: string;
  onAvatarChange: (avatarPath: string | null) => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatar,
  onAvatarChange
}) => {
  const { updateUser, user } = useUser();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Apenas arquivos de imagem são permitidos (JPEG, PNG, GIF)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const response = await uploadUserAvatar(file);
      const userData = response.data.data.user;
      
      // Update user context and notify parent
      updateUser(userData);
      onAvatarChange(userData.avatar);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao fazer upload do avatar');
      console.error('Avatar upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setUploading(true);
      setError(null);

      const response = await removeUserAvatar();
      const userData = response.data.data.user;
      
      // Update user context and notify parent
      updateUser(userData);
      onAvatarChange(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao remover avatar');
      console.error('Avatar removal error:', err);
    } finally {
      setUploading(false);
    }
  };

  const getInitials = (name: string = '') => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
          {currentAvatar ? (
            <img
              src={currentAvatar.startsWith('http') ? currentAvatar : `${config.apiUrl.replace('/api', '')}${currentAvatar}`}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-white text-xl font-semibold">
              {getInitials(user?.name || 'User')}
            </span>
          )}
        </div>
        
        {/* Upload Button */}
        <div className="absolute -bottom-1 -right-1">
          <label
            htmlFor="avatar-upload"
            className="w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center cursor-pointer transition-colors"
          >
            {uploading ? (
              <RotateCcw className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Camera className="w-4 h-4 text-white" />
            )}
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
        </div>
      </div>

      <div className="text-center">
        <p className="text-sm text-foreground-secondary mb-2">
          Clique na câmera para alterar
        </p>
        <p className="text-xs text-foreground-tertiary">
          Formatos suportados: JPEG, PNG, GIF (máx. 5MB)
        </p>
      </div>

      {currentAvatar && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleRemoveAvatar}
          disabled={uploading}
          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
        >
          <X className="w-4 h-4 mr-2" />
          Remover Avatar
        </Button>
      )}

      {error && (
        <div className="text-red-400 text-sm text-center">
          {error}
        </div>
      )}
    </div>
  );
};

export default AvatarUpload;